import React from "react";
import {
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Link,
    Typography,
    TableCell,
    Box,
    Popover,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { StyledTableRow, StyledTableCell } from "src/components/Table";

const useStyles = makeStyles((theme) => ({
    table: {
        minWidth: 650,
    },
    statusReady: {
        backgroundColor: "#4caf50",
        color: "white",
    },
    statusInvalid: {
        backgroundColor: "#f44336",
        color: "white",
    },
    statusUnassigned: {
        backgroundColor: "#9e9e9e",
        color: "white",
    },
    urlCell: {
        maxWidth: 200,
        minWidth: 100,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    attributeChip: {
        margin: theme.spacing(0.25),
        height: '20px',
        fontSize: '0.7rem',
        '& .MuiChip-label': {
            padding: '0 6px',
        },
    },
    moreChip: {
        margin: theme.spacing(0.25),
        cursor: 'pointer',
        height: '20px',
        fontSize: '0.7rem',
        '& .MuiChip-label': {
            padding: '0 6px',
        },
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    productName: {
        maxWidth: 150,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    // '&:hover': {
    //     whiteSpace: 'normal',
    //     overflow: 'visible',
    //     },
    },
}));

export default function UnassignAvailabilityListTableComponent({
    validatedItems, // array of validated itemd
    isUpdating,
    selectedMethod,
}) {
    const classes = useStyles();

    // Popover state for attribute expansion
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [popoverAttributes, setPopoverAttributes] = React.useState([]);

    const handlePopoverOpen = (event, attributes) => {
        setAnchorEl(event.currentTarget);
        setPopoverAttributes(attributes);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
        setPopoverAttributes([]);
    };

    const isPopoverOpen = Boolean(anchorEl);

    const maxRows = 10;
    const rowHeight = 53;
    const tableContainerStyle = {
        maxHeight: `${rowHeight * maxRows}px`,
        overflowY: "auto",
    };

    const renderTableHeaders = () => {
        const type = validatedItems && validatedItems.length > 0
            ? validatedItems[0].type
            : (selectedMethod || "url");

        if (type === "url") {
            return (
                <>
                  <TableCell>No</TableCell>
                  <TableCell>Link</TableCell>
                  <TableCell>Serial Number</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell>Status</TableCell>
                </>
            );
        }
        if (type === "seq_num" || type === "serial_num") {
            return (
                <>
                  <TableCell>No</TableCell>
                  <TableCell>Serial From</TableCell>
                  <TableCell>Serial To</TableCell>
                  <TableCell>Sequence From</TableCell>
                  <TableCell>Sequence To</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Batch Number</TableCell>
                </>
            );
        }
        return null;
    };

    const renderProductAttributes = (attributes) => {
        if (!attributes || !Array.isArray(attributes) || attributes.length === 0) {
            return null;
        }

        const MAX_VISIBLE = 3;
        const visibleAttributes = attributes.slice(0, MAX_VISIBLE);
        const remainingAttributes = attributes.slice(MAX_VISIBLE);
        const remainingCount = remainingAttributes.length;
        const hasMore = remainingCount > 0;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {visibleAttributes.map((attr, idx) => (
                        <Chip
                          key={idx}
                          label={attr}
                          variant="outlined"
                          size="small"
                          className={classes.attributeChip}
                        />
                    ))}
                </Box>
                {hasMore && (
                    <Box sx={{ display: 'flex', flexWrap: "wrap", gap: 0.5 }}>
                        <Chip
                          label={`+${remainingCount}`}
                          variant="outlined"
                          size="small"
                          className={classes.moreChip}
                          onClick={(event) => handlePopoverOpen(event, remainingAttributes)}
                          clickable
                        />
                    </Box>
                )}
            </Box>
        );
    };

    const renderTableRow = (item, rowIndex) => {
        // Determine status label and class
        let statusLabel = "Could Not Unassign";
        let statusClass = classes.statusInvalid;

        if (item.status === 1) {
            statusLabel = "Ready to Unassign";
            statusClass = classes.statusReady;
        } else if (item.status === 2 && item.reason === "unassigned") {
            statusLabel = "Unassigned";
            statusClass = classes.statusUnassigned;
        }

        const statusChip = (
            <Chip
              label={statusLabel}
              className={statusClass}
              size="small"
            />
        );

        if (item.type === "url") {
            return (
                <StyledTableRow key={item.index}>
                    <StyledTableCell>{rowIndex + 1}</StyledTableCell>
                    <StyledTableCell className={classes.urlCell}>
                        <Link href={item.url} target="_blank" rel="noopener noreferrer">
                          {item.url}
                        </Link>
                    </StyledTableCell>
                    <StyledTableCell>
                        <Typography variant="body2">
                            {item.serial_number || "-"}
                        </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2" className={classes.productName}>
                                {item.product_name || "-"}
                            </Typography>
                            {renderProductAttributes(item.product_attributes)}
                        </Box>
                    </StyledTableCell>
                    <StyledTableCell>{statusChip}</StyledTableCell>
                </StyledTableRow>
            );
        }
        if (item.type === "seq_num" || item.type === "serial_num") {
            const batchNumber = item.batch_number || item.batch || "-";
            const serialFrom = item.from_serial || "-";
            const serialTo = item.to_serial || "-";
            const sequenceFrom = item.from_seq || "-";
            const sequenceTo = item.to_seq || "-";
            const productName = item.product_name || "-";
            return (
                <StyledTableRow key={item.index}>
                    <StyledTableCell>{rowIndex + 1}</StyledTableCell>
                    <StyledTableCell>{serialFrom}</StyledTableCell>
                    <StyledTableCell>{serialTo}</StyledTableCell>
                    <StyledTableCell>{sequenceFrom}</StyledTableCell>
                    <StyledTableCell>{sequenceTo}</StyledTableCell>
                    <StyledTableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2" className={classes.productName}>
                                {productName}
                            </Typography>
                            {renderProductAttributes(item.product_attributes)}
                        </Box>
                    </StyledTableCell>
                    <StyledTableCell>{statusChip}</StyledTableCell>
                    <StyledTableCell>{batchNumber}</StyledTableCell>
                </StyledTableRow>
            );
        }
        return null;
    };

    const type = validatedItems && validatedItems.length > 0
        ? validatedItems[0].type
        : (selectedMethod || "url");

    const colSpanCount = type === "url" ? 5 : 8;

    return (
    <Box className={classes.tableWrapper}>
        <TableContainer component={Paper} style={tableContainerStyle}>
            <Table className={classes.table}>
                <TableHead>
                    <TableRow>{renderTableHeaders()}</TableRow>
                </TableHead>
                <TableBody>
                    {(!validatedItems || validatedItems.length === 0) ? (
                        <TableRow>
                            <TableCell align="center" colSpan={colSpanCount}>
                                <Typography variant="body2" color="textSecondary" align="center">
                                    No Data
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : (
                        validatedItems.map((item, index) => renderTableRow(item, index))
                    )}
                </TableBody>
            </Table>

            <Popover
              open={isPopoverOpen}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              disableRestoreFocus
              anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
              }}
              transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
              }}
              PaperProps={{
                  style: { padding: '12px', maxWidth: '300px' }
              }}
            >
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {popoverAttributes.map((attr, idx) => (
                      <Chip
                        key={idx}
                        label={attr}
                        variant="outlined"
                        size="small"
                        className={classes.attributeChip}
                      />
                    ))}
                </Box>
            </Popover>
        </TableContainer>
    </Box>
);
}
